import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtTime, fmtDate, STATUS } from "@/lib/api";
import { SessionActions } from "@/components/SessionActions";
import { SessionForm } from "@/components/SessionForm";
import { DayView } from "@/components/DayView";
import { StatusChip, Modal } from "@/components/ui-bits";
import { Avatar } from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";

const isPhone = () => typeof window !== "undefined" && window.innerWidth < 768;
const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const localKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const gridFor = (m) => { const first = monthStart(m); const lead = (first.getDay() + 6) % 7; const start = new Date(first); start.setDate(1 - lead); return Array.from({ length: 42 }, (_, i) => { const x = new Date(start); x.setDate(start.getDate() + i); return x; }); };
const dotClass = (s) => (s.status === "COMPLETED" ? "done" : s.status === "AWAITING_CONFIRMATION" || s.status === "CHANGE_REQUESTED" ? "warn" : s.status === "CANCELLED" || s.status === "NO_SHOW" ? "off" : "ok");

const movable = (s) => !["COMPLETED", "CANCELLED", "NO_SHOW"].includes(s.status);
const startDrag = (e, s) => { e.dataTransfer.setData("text/plain", s.id); e.dataTransfer.effectAllowed = "move"; };

const MonthGrid = ({ month, byDay, selected, onPick, compact, onDrop, dragOver, setDragOver }) => {
  const todayK = localKey(new Date());
  return (
    <div className={`cal ${compact ? "compact" : ""}`} data-testid={`month-${month.getFullYear()}-${month.getMonth() + 1}`}>
      <div className="cal-title">{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
      <div className="cal-head">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <span key={d}>{d}</span>)}</div>
      <div className="cal-grid">
        {gridFor(month).map((d) => {
          const k = localKey(d); const list = byDay[k] || []; const out = d.getMonth() !== month.getMonth();
          return (
            <button type="button" key={k} className={`cal-day ${out ? "out" : ""} ${k === todayK ? "today" : ""} ${selected === k ? "sel" : ""} ${dragOver === k ? "over" : ""} ${k < todayK ? "past" : ""}`} onClick={() => onPick(k)} data-testid={`cal-day-${k}`}
              onDragOver={(e) => { if (k >= todayK) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOver !== k) setDragOver(k); } }}
              onDragLeave={() => dragOver === k && setDragOver(null)}
              onDrop={(e) => { e.preventDefault(); setDragOver(null); const id = e.dataTransfer.getData("text/plain"); if (id && k >= todayK) onDrop(id, k); }}>
              <span className="n">{d.getDate()}</span>
              {list.length > 0 && <span className="dots" data-testid={`cal-dots-${k}`}>{list.slice(0, compact ? 3 : 5).map((s) => <i key={s.id} className={dotClass(s)} draggable={movable(s)} onDragStart={(e) => { e.stopPropagation(); startDrag(e, s); }} title={`${fmtTime(s.startAt)} · ${s.clientName}${movable(s) ? " — drag to move" : ""}`} data-testid={`cal-dot-${s.id}`} />)}{list.length > (compact ? 3 : 5) && <em>+{list.length - (compact ? 3 : 5)}</em>}</span>}
              {!compact && list.length > 0 && <span className="cnt">{list.length}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const slotsFor = (avail) => {
  const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  const wins = avail?.windows?.length ? avail.windows : [{ start: "06:00", end: "20:00" }];
  const start = Math.min(...wins.map((w) => toMin(w.start))), end = Math.max(...wins.map((w) => toMin(w.end)));
  const step = avail?.slotStepMin || 30;
  const out = [];
  for (let m = start; m < end; m += step) out.push({ min: m, label: `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}` });
  return out;
};
const minutesOf = (iso) => { const d = new Date(iso); return d.getHours() * 60 + d.getMinutes(); };

const Timeline = ({ dateKey, sessions, avail, onDropSlot, onOpen, dragSlot, setDragSlot }) => {
  const slots = slotsFor(avail);
  const step = avail?.slotStepMin || 30;
  const past = dateKey < localKey(new Date());
  const nowMin = dateKey === localKey(new Date()) ? minutesOf(new Date().toISOString()) : -1;
  const outside = sessions.filter((s) => { const m = minutesOf(s.startAt); return m < slots[0]?.min || m >= (slots.at(-1)?.min ?? 0) + step; });
  return (
    <div className="timeline" data-testid="day-timeline">
      {slots.map((sl) => {
        const here = sessions.filter((s) => { const m = minutesOf(s.startAt); return m >= sl.min && m < sl.min + step; });
        const slotPast = past || (nowMin >= 0 && sl.min + step <= nowMin);
        return (
          <div key={sl.label} className={`tslot ${here.length ? "busy" : ""} ${dragSlot === sl.label ? "over" : ""} ${slotPast ? "past" : ""}`} data-testid={`time-slot-${sl.label}`}
            onDragOver={(e) => { if (!slotPast) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragSlot !== sl.label) setDragSlot(sl.label); } }}
            onDragLeave={() => dragSlot === sl.label && setDragSlot(null)}
            onDrop={(e) => { e.preventDefault(); setDragSlot(null); const id = e.dataTransfer.getData("text/plain"); if (id && !slotPast) onDropSlot(id, sl.label); }}>
            <span className="tlabel num">{sl.label}</span>
            <div className="tbody">
              {here.map((s) => (
                <div role="button" tabIndex={0} key={s.id} className={`tchip ${movable(s) ? "draggable" : "closed"}`} draggable={movable(s)} onDragStart={(e) => startDrag(e, s)} onClick={() => onOpen(s)} onKeyDown={(e) => e.key === "Enter" && onOpen(s)} data-testid={`timeline-session-${s.id}`} title={`${fmtTime(s.startAt)}–${fmtTime(s.endAt)} · ${s.title}`}>
                  <Avatar client={{ id: s.clientId, photoKey: s.clientPhotoKey, name: s.clientName }} size={20} radius={5} /><b>{s.clientName}</b><span className="ss">{fmtTime(s.startAt)} · {s.title}</span><i className={`sdot ${dotClass(s)}`} />
                </div>
              ))}
              {!here.length && !slotPast && <span className="tfree">drop here</span>}
            </div>
          </div>
        );
      })}
      {outside.length > 0 && <div className="ss" style={{ marginTop: 6 }} data-testid="timeline-outside">{outside.length} session{outside.length !== 1 ? "s" : ""} outside working hours: {outside.map((s) => `${fmtTime(s.startAt)} ${s.clientName}`).join(", ")}</div>}
    </div>
  );
};

export default function Schedule() {
  const [month, setMonth] = useState(() => { const q = new URLSearchParams(window.location.search).get("date"); return monthStart(q ? new Date(q + "T00:00:00") : new Date()); });
  const [view, setView] = useState(() => (isPhone() ? "day" : "month"));
  const [sessions, setSessions] = useState(null);
  const [selected, setSelected] = useState(() => new URLSearchParams(window.location.search).get("date") || localKey(new Date()));
  const [adding, setAdding] = useState(null);
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [coach, setCoach] = useState(() => localStorage.getItem("spottr-schedule-coach") || "all");
  useEffect(() => {
    api.get("/team").then((r) => {
      const members = r.data.members || []; setTeam(members);
      const stored = localStorage.getItem("spottr-schedule-coach");
      const mine = members.find((m) => m.id === user?.id);
      if (!stored && members.length > 1 && mine && !mine.isOwner) setCoach(user.id);
    }).catch(() => {});
  }, [user]);
  const pickCoach = (v) => { setCoach(v); localStorage.setItem("spottr-schedule-coach", v); };
  const ownerId = team.find((m) => m.isOwner)?.id;
  const visible = useMemo(() => {
    if (!sessions || coach === "all" || team.length < 2) return sessions;
    const ids = new Set(team.map((m) => m.id));
    return sessions.filter((s) => (ids.has(s.trainerId) ? s.trainerId : ownerId) === coach);
  }, [sessions, coach, team, ownerId]);
  const [sel, setSel] = useState(null);
  const [dayIdx, setDayIdx] = useState(0);
  const [dragOver, setDragOver] = useState(null);
  const [dragSlot, setDragSlot] = useState(null);
  const [panelMode, setPanelMode] = useState("timeline");
  const [dragging, setDragging] = useState(false);
  const [avail, setAvail] = useState(null);
  useEffect(() => { api.get("/settings/availability").then((r) => setAvail(r.data)).catch(() => setAvail({})); }, []);
  const range = useMemo(() => ({ start: gridFor(month)[0], end: (() => { const e = gridFor(addMonths(month, view === "two" ? 1 : 0))[41]; const x = new Date(e); x.setDate(x.getDate() + 1); return x; })() }), [month, view]);
  const load = useCallback(() => api.get("/sessions", { params: { start: range.start.toISOString(), end: range.end.toISOString() } }).then((r) => setSessions(r.data)), [range]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session");
    if (!sid || !sessions) return;
    const s = sessions.find((x) => x.id === sid);
    if (s) { setSel(s); window.history.replaceState({}, "", window.location.pathname); }
  }, [sessions]);
  const byDay = useMemo(() => { const m = {}; (visible || []).forEach((s) => { (m[localKey(new Date(s.startAt))] ||= []).push(s); }); Object.values(m).forEach((l) => l.sort((a, b) => a.startAt.localeCompare(b.startAt))); return m; }, [visible]);
  const daySessions = byDay[selected] || [];
  const attention = (visible || []).filter((s) => (s.status === "AWAITING_CONFIRMATION" || s.status === "CHANGE_REQUESTED") && new Date(s.startAt) > new Date());
  const current = sessions?.find((s) => s.id === sel?.id) || sel;
  const applyMove = useCallback(async (sid, targetIso, silent, label) => {
    try {
      await api.patch(`/sessions/${sid}`, { action: "move", startAt: targetIso, note: silent ? "silent" : null });
      setSelected(localKey(new Date(targetIso)));
      if (label) toast.success(label);
      load();
    } catch (ex) { toast.error(errMsg(ex)); load(); }
  }, [load]);
  const moveTo = useCallback(async (sid, dateKey, time = null) => {
    const s = (sessions || []).find((x) => x.id === sid);
    if (!s) return;
    const old = new Date(s.startAt);
    const target = new Date(`${dateKey}T00:00:00`);
    if (time) { const [h, m] = time.split(":").map(Number); target.setHours(h, m, 0, 0); } else target.setHours(old.getHours(), old.getMinutes(), 0, 0);
    if (target.getTime() === old.getTime()) return;
    if (!movable(s)) return toast.error("Closed sessions can't be moved");
    if (target < new Date()) return toast.error("Can't move a session into the past");
    const dur = new Date(s.endAt) - old;
    setSessions((list) => list.map((x) => (x.id === sid ? { ...x, startAt: target.toISOString(), endAt: new Date(target.getTime() + dur).toISOString() } : x)));
    try {
      await api.patch(`/sessions/${sid}`, { action: "move", startAt: target.toISOString() });
      setSelected(dateKey);
      toast.success(`${s.clientName} moved to ${fmtDate(target.toISOString(), { weekday: "short", day: "numeric", month: "short" })} ${fmtTime(target.toISOString())}`,
        { action: { label: "Undo", onClick: () => applyMove(sid, old.toISOString(), true, `Moved back to ${fmtDate(old.toISOString(), { weekday: "short", day: "numeric", month: "short" })}`) }, duration: 6000 });
      load();
    } catch (ex) { toast.error(errMsg(ex)); load(); }
  }, [sessions, load, applyMove]);
  const weekDays = useMemo(() => { const d = new Date(selected + "T00:00:00"); const mon = new Date(d); mon.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return Array.from({ length: 7 }, (_, i) => { const x = new Date(mon); x.setDate(mon.getDate() + i); return x; }); }, [selected]);

  return (
    <div className="fade" data-testid="admin-schedule">
      <div className="sched-bar">
        <div className="actions">
          <button className="btn g sm" onClick={() => setMonth(addMonths(month, -1))} data-testid="month-prev-button">← Prev</button>
          <button className="btn g sm" onClick={() => { setMonth(monthStart(new Date())); setSelected(localKey(new Date())); }} data-testid="month-today-button">Today</button>
          <button className="btn g sm" onClick={() => setMonth(addMonths(month, 1))} data-testid="month-next-button">Next →</button>
          <h3 style={{ margin: "0 0 0 8px" }} data-testid="schedule-title">{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}{view === "two" ? ` – ${addMonths(month, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" })}` : ""}</h3>
        </div>
        <div className="actions">
          {team.length > 1 && (
            <label className="coach-filter" data-testid="coach-filter">
              <span>Coach</span>
              <select value={coach} onChange={(e) => pickCoach(e.target.value)} data-testid="coach-filter-select">
                <option value="all">All coaches</option>
                {team.map((m) => <option key={m.id} value={m.id}>{m.id === user?.id ? "Me" : m.name}{m.isOwner ? " · owner" : ""}</option>)}
              </select>
            </label>
          )}
          <div className="tabs" style={{ margin: 0 }}>
            {isPhone() && <button className={view === "day" ? "on" : ""} onClick={() => setView("day")} data-testid="view-day-button">Day</button>}
            <button className={view === "month" ? "on" : ""} onClick={() => setView("month")} data-testid="view-month-button">Month</button>
            <button className={view === "two" ? "on" : ""} onClick={() => setView("two")} data-testid="view-two-month-button">2 Months</button>
          </div>
          <button className="btn sm" onClick={() => setAdding({ date: selected })} data-testid="add-session-button">+ Add session</button>
        </div>
      </div>
      {!sessions ? <div className="center"><span className="spin" /></div> : view === "day" ? (
        <div className="panel"><DayView days={weekDays} sessions={visible} dayIdx={dayIdx} setDayIdx={setDayIdx} onDone={load} onAdd={(k) => setAdding({ date: k })} onOpen={setSel} /></div>
      ) : (
        <div className={`sched ${view === "two" ? "two" : ""}`}>
          <div className={`sched-cal ${view === "two" ? "two" : ""}`}>
            <MonthGrid month={month} byDay={byDay} selected={selected} onPick={setSelected} compact={view === "two"} onDrop={moveTo} dragOver={dragOver} setDragOver={setDragOver} />
            {view === "two" && <MonthGrid month={addMonths(month, 1)} byDay={byDay} selected={selected} onPick={setSelected} compact onDrop={moveTo} dragOver={dragOver} setDragOver={setDragOver} />}
          </div>
          <aside className={`panel tight sched-day ${dragging ? "dragging" : ""}`} data-testid="day-panel" onDragStart={() => setDragging(true)} onDragEnd={() => setDragging(false)} onDrop={() => setDragging(false)}>
            <div className="ph"><h3 data-testid="day-panel-title">{fmtDate(new Date(selected + "T12:00:00").toISOString(), { weekday: "long", day: "numeric", month: "short" })}</h3>
              <div className="actions">
                <div className="tabs mini" style={{ margin: 0 }}>
                  <button type="button" className={panelMode === "timeline" ? "on" : ""} onClick={() => setPanelMode("timeline")} data-testid="panel-mode-timeline">Times</button>
                  <button type="button" className={panelMode === "list" ? "on" : ""} onClick={() => setPanelMode("list")} data-testid="panel-mode-list">List</button>
                </div>
                <button className="btn sm" onClick={() => setAdding({ date: selected })} data-testid="day-panel-add">+ Add</button>
              </div>
            </div>
            {panelMode === "timeline" && <div className="scroll"><div className="ss" style={{ marginBottom: 4 }} data-testid="drag-hint">Drag a session onto a time slot — or onto any calendar date — to move it · client notified</div><Timeline dateKey={selected} sessions={daySessions} avail={avail} onDropSlot={(sid, time) => moveTo(sid, selected, time)} onOpen={setSel} dragSlot={dragSlot} setDragSlot={setDragSlot} /></div>}
            {panelMode === "list" && <div className="scroll">
              {daySessions.length === 0 && <div className="ss" data-testid="day-panel-empty">No sessions — tap + Add to book one, or drag one here from the calendar.</div>}
              {daySessions.length > 0 && <div className="ss" style={{ marginBottom: 4 }} data-testid="drag-hint-list">Drag a session onto any date to move it · same time, client notified</div>}
              {daySessions.map((s) => (
                <div className={`row ${movable(s) ? "draggable" : ""}`} key={s.id} data-testid={`day-session-${s.id}`} onClick={() => setSel(s)} role="button" draggable={movable(s)} onDragStart={(e) => startDrag(e, s)} title={movable(s) ? "Drag onto a calendar date to move" : ""}>
                  <span className="grip" aria-hidden="true">⋮⋮</span>
                  <span className="num tm">{fmtTime(s.startAt)}</span>
                  <Avatar client={{ id: s.clientId, photoKey: s.clientPhotoKey, name: s.clientName }} size={26} radius={7} />
                  <b className="nm">{s.clientName}</b>
                  <span className="ss ty">{s.title}{s.seriesId ? " · weekly" : s.selfBooked ? " · self-booked" : ""}</span>
                  <span className={`chip ${STATUS[s.status]?.[0] ?? "gray"}`}><span className="dot" />{s.status === "AWAITING_CONFIRMATION" ? "Pending" : s.status === "CHANGE_REQUESTED" ? "Change" : STATUS[s.status]?.[1]}</span>
                </div>
              ))}
            </div>}
            {!(dragging && panelMode === "timeline") && <><div className="ph" style={{ marginTop: 10 }}><h3>Needs attention · {attention.length}</h3></div>
            <div className="scroll" style={{ maxHeight: 180 }}>
              {attention.length === 0 && <div className="ss">Everything upcoming is confirmed.</div>}
              {attention.map((s) => (
                <div className="row" key={s.id} data-testid={`attention-${s.id}`} onClick={() => setSel(s)} role="button">
                  <span className="num tm">{fmtDate(s.startAt, { day: "numeric", month: "short" })} {fmtTime(s.startAt)}</span>
                  <b className="nm">{s.clientName}</b>
                  <span className="ss ty">{s.changeNote ? `wants: ${s.changeNote}` : s.title}</span>
                  <StatusChip status={s.status} />
                </div>
              ))}
            </div>
            </>}
          </aside>
        </div>
      )}
      {adding && <SessionForm defaultDate={adding.date} onClose={() => setAdding(null)} onSaved={() => { setAdding(null); load(); }} />}
      {current && (
        <Modal title={current.title.toUpperCase()} sub={`${current.clientName} · ${fmtDate(current.startAt, { weekday: "long", day: "numeric", month: "short" })} ${fmtTime(current.startAt)}–${fmtTime(current.endAt)}`} onClose={() => setSel(null)} testId="session-detail-modal">
          <div className="kv"><span>Status</span><StatusChip status={current.status} /></div>
          <div className="kv"><span>Focus</span><span>{current.focus || "—"}</span></div>
          {current.changeNote && <div className="kv"><span>Client wants</span><span>{current.changeNote}</span></div>}
          <div className="kv"><span>Prep note</span><span style={{ textAlign: "right" }}>{current.prepNote || "—"}</span></div>
          <div className="kv"><span>Confirmed via</span><span>{current.source || "—"}</span></div>
          <div className="mfoot" style={{ justifyContent: "space-between" }}>
            <SessionActions session={current} admin onDone={() => { load(); }} />
            <button className="btn g" onClick={() => setSel(null)} data-testid="session-detail-close">Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
